import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { PrintFrameService } from '../../services/print-frame.service';
import { AppStateService } from '../../services/app-state.service';
import { AuthService } from '../../services/auth.service';
import { UploadDialogService } from '../../components/upload-dialog/upload-dialog.component';
import { ValidationDialogService } from '../../components/validation-dialog/validation-dialog.component';
import { BaseAPIService } from '../../services/base-api.service';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-product-customization',
  templateUrl: './product-customization.component.html'
})
export class ProductCustomizationComponent implements OnInit, OnDestroy {
  private sub = new Subscription();

  // Query params
  productSlug = '';
  productTypeSlug = '';

  // Stock image from wall-art search
  stockImageId = '';
  stockImageData: any = null;

  // State
  isLoading = true;
  activeTab = 'photo'; // Upload Image tab opens first
  priceLoading = false;
  addingToCart = false;
  cartSuccess = false;
  errorMsg = '';

  // ── Data from init API ──
  productTypes: any[] = [];
  product: any = null;
  productType: any = null;
  printSizes: string[] = [];
  papers: any[] = [];
  frameStyles: any[] = [];
  frameMaterials: any[] = [];
  frameColors: string[] = [];
  frameWidths: string[] = [];
  frames: any[] = [];
  glasses: any[] = [];
  matColors: any[] = [];
  matWidths: number[] = []; // DB-driven (init.matWidths → settings.p2f.mat_widths)
  mounts: any[] = [];
  canvasWraps: any[] = [];
  qualities: any[] = [];
  p2fFilters: any[] = [];
  // Image filters (Normal/Grayscale/Sepia/…) — DB-driven from init.imageFilters,
  // the SAME list the sales panel shows. Each is { name, css }.
  imageFilters: { name: string; css: string }[] = [];

  // ── Selected values ──
  printSize = '';
  printSizeCustomWidth = '';
  printSizeCustomHeight = '';
  printSurface: any = null;
  frame: any = null;
  glass: any = null;
  mount1: any = null;
  mount2: any = null;
  mount3: any = null;
  mountSize1 = 1.5;
  mountSize2 = 0;
  mountSize3 = 0;
  isShowMount = false; // "Without Mount" by default
  canvasWrap: any = null;
  qualityGrade = 'Frame';
  isTableFrame = 0;
  selectedImageFilter = ''; // filter NAME; '' = Normal / none
  roundedShape = false;     // oval/rounded frame (framing & frame_only only)

  // Size limits
  maxCustomizeWidth = 60;
  maxCustomizeHeight = 60;

  // ── Frame filters ──
  selectedFrameWidth = '1'; // Default 1" — must always be selected
  selectedFrameMaterial = 0;
  selectedFrameStyle = 0;
  selectedFrameColor = '';
  defaultFrame = '';
  allowedMaterialIds: number[] = [];
  allowedStylesIds: number[] = [];
  allowedColors: string[] = [];

  // ── Upload ──
  myPhotos: string[] = [];
  activePhotoIndex = 0;
  uploading = false;

  // ── Price ──
  payable: number = 0;
  discount: number = 0;
  pricingDetails: any = null;
  priceBreakdown: any = null;

  // ── Live preview ──
  imagePath = '';
  frameBorderWidth = 0;
  frameBorderImage = '';
  mount1Background = '#fff';
  mount2Background = '#fff';
  mount3Background = '#fff';
  mat1Padding = 0;
  mat2Padding = 0;
  mat3Padding = 0;
  canvasWrapSideBackground = '#000';
  canvasWrapItem = '';

  // Preview dimension calculation
  private readonly PREVIEW_CONTAINER = 500;
  private readonly PREVIEW_CONTAINER_MOBILE = 350;
  private readonly FRAME_BORDER_CALC = 10;
  private readonly MAT_CALC = 6;
  private readonly ROOM_INCH = 8;

  // Computed preview sizes
  previewW = '270px';
  previewH = '180px';
  previewRoomW = '96px';
  previewRoomH = '144px';

  // ── Room preview ──
  showRoomPreview = false;
  roomType = 'sofa_room';
  rooms = [
    { slug: 'bed_room', label: 'Bedroom', image: 'assets/images/mahattaart/product_customization/bed_room_room.png', thumb: 'assets/images/mahattaart/product_customization/bed_room_room_thumb.jpg' },
    { slug: 'chair_room', label: 'Chair Room', image: 'assets/images/mahattaart/product_customization/chair_room.png', thumb: 'assets/images/mahattaart/product_customization/chair_room_thumb.jpg' },
    { slug: 'sofa_room', label: 'Sofa Room', image: 'assets/images/mahattaart/product_customization/sofa_room.png', thumb: 'assets/images/mahattaart/product_customization/sofa_room_thumb.jpg' },
    { slug: 'sofa_white_room', label: 'White Sofa', image: 'assets/images/mahattaart/product_customization/sofa_white_room.png', thumb: 'assets/images/mahattaart/product_customization/sofa_white_room_thumb.jpg' },
  ];

  // ── Tabs ──
  allTabs = [
    { slug: 'product', name: 'Product', product_type: 'framing,print_only,art_print,poster,canvas,frame_only' },
    { slug: 'photo', name: 'Upload Image', product_type: 'framing,print_only,art_print,poster,canvas,frame_only' },
    { slug: 'print', name: 'Print Surface', product_type: 'framing,print_only,art_print,poster,canvas' },
    { slug: 'mount', name: 'Mount & Glass', product_type: 'framing,frame_only' },
    { slug: 'frames', name: 'Frames', product_type: 'framing,frame_only' },
    { slug: 'canvas', name: 'Canvas', product_type: 'canvas' }
  ];
  visibleTabs: any[] = [];

  tabIcons: Record<string, string> = {
    product: 'assets/images/mahattaart/product_icons/size_icon.jpg',
    photo: 'assets/images/mahattaart/product_icons/upload_photos.png',
    print: 'assets/images/mahattaart/product_icons/print_icon.jpg',
    mount: 'assets/images/mahattaart/product_icons/mat_icon.jpg',
    frames: 'assets/images/mahattaart/product_icons/frame_icon.jpg',
    canvas: 'assets/images/mahattaart/product_icons/canvas_gallery.jpg',
  };

  private readonly productTypeImages: Record<string, string> = {
    canvas:     'https://storage.googleapis.com/static-mahattaart-online/product_type/frame_photos/canvas.jpg',
    framing:    'https://storage.googleapis.com/static-mahattaart-online/product_type/frame_photos/photo_frame.jpg',
    print_only: 'https://storage.googleapis.com/static-mahattaart-online/product_type/frame_photos/print.jpg',
    frame_only: 'https://storage.googleapis.com/static-mahattaart-online/product_type/frame_photos/frame.jpg',
  };

  // ── Contact form (Reactive) ──
  queryForm!: FormGroup;
  formSubmitting = false;
  formSuccess = false;
  formError = false;

  // Reviews
  reviews = [
    { name: 'Saurabh Verma', text: 'Mahattaart is the best I was very Excited about my product and when it arrived I was very Happy, The Quality is A1 and ...', rating: 5 },
    { name: 'Sonal Hatila', text: 'I have been Ordering From MahattaArt from 2 years now and they never fail to impress me with their Quality of products a...', rating: 5 },
    { name: 'Its Kinda Cute', text: 'Good work', rating: 5 },
    { name: 'Selvam A', text: 'Received a very good work! Packing is very neat and secure. Really I\'m very happy for my order. Worth of every penny. Th...', rating: 5 },
    { name: 'Moeiz Huda', text: 'Excellent service and professionalism. I had ordered for a photo to be printed and framed. It was supposed to be my anni...', rating: 5 },
  ];
  reviewScrollIndex = 0;

  constructor(
    private route: ActivatedRoute,
    public router: Router,
    private location: Location,
    private http: HttpClient,
    private fb: FormBuilder,
    private pfService: PrintFrameService,
    private state: AppStateService,
    private auth: AuthService,
    private uploadDialog: UploadDialogService,
    private validationDialog: ValidationDialogService,
    private baseApiService: BaseAPIService
  ) {}

  // ════════════════════════════════════════════════════════
  //  LIFECYCLE
  // ════════════════════════════════════════════════════════

  ngOnInit() {
    // Init reactive contact form
    this.queryForm = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(2)]],
      email: ['', [Validators.required, Validators.email]],
      contact: ['', [Validators.required, Validators.pattern(/^[0-9]{10}$/)]],
      requirement: ['', [Validators.required, Validators.minLength(5)]]
    });

    this.sub.add(
      this.route.queryParams.subscribe(params => {
        this.productSlug = params['product'] || 'frame_your_photos';
        this.productTypeSlug = params['product_type'] || 'framing';
        this.stockImageId = params['image_id'] || '';

        this.init();

        // Load stock image by image_id from wall-art search
        if (this.stockImageId) {
          this.loadStockImage(this.stockImageId);
        }
      })
    );

    // Listen for upload dialog results
    this.sub.add(
      this.uploadDialog.result$.subscribe(result => {
        if (result && result.url) {
          this.myPhotos.push(result.url);
          this.activePhotoIndex = this.myPhotos.length - 1;
          this.imagePath = result.url;
          this.updatePreview();
          this.updateCanvasWrapSideBackground();
        }
      })
    );
  }

  ngOnDestroy() {
    this.sub.unsubscribe();
    this.uploadDialog.close();
  }

  // ════════════════════════════════════════════════════════
  //  1. INIT — FIRST PAGE LOAD
  // ════════════════════════════════════════════════════════

  private init() {
    this.isLoading = true;
    this.activeTab = 'photo';
    this.myPhotos = [];
    this.activePhotoIndex = 0;
    this.payable = 0;
    this.discount = 0;
    this.cartSuccess = false;
    this.errorMsg = '';
    this.isShowMount = false; // "Without Mount" by default
    window.scrollTo({ top: 0, behavior: 'smooth' });

    this.pfService.initProductCustomization(this.productSlug, this.productTypeSlug, 0, this.stockImageId).subscribe({
      next: (res: any) => {
        this.handleInitResponse(res);
        this.autoOpenUploadDialog();
      },
      error: () => {
        this.fallbackInit();
        this.autoOpenUploadDialog();
      }
    });
  }

  private handleInitResponse(res: any) {
    // Which tab opens first comes from the shared P2F config (settings.p2f.default_tab),
    // the SAME value the sales panel reads — so both default to the same tab. Set
    // before filterTabs() so it sticks when the tab is visible.
    if (res?.defaultTab) {
      this.activeTab = res.defaultTab;
    }
    // Product types
    this.productTypes = (res?.productTypes?.data || res?.productTypes || [])
      .map((pt: any) => pt.image ? pt : { ...pt, image: this.productTypeImages[pt.slug] || '' });
    // For wall_art, only show relevant product types
    if (this.productSlug === 'wall_art') {
      const wallArtSlugs = ['canvas', 'framing', 'art_print'];
      this.productTypes = this.productTypes.filter((pt: any) => wallArtSlugs.includes(pt.slug));
    }
    this.product = res?.product || null;
    this.productType = res?.productType || null;
    this.p2fFilters = res?.p2fFilters?.data || [];

    // Print sizes
    // DB-driven only (init.printSizes → settings.p2f.image_sizes). No hardcoded
    // fallback: `?? []` (not `||`) so an empty list stays empty and is not silently
    // replaced — an empty list means the config must be seeded.
    this.printSizes = res?.printSizes ?? [];
    // Image filters from the shared P2F config (settings.p2f.image_filters), the
    // same source the sales panel reads. DB-only, no hardcoded fallback.
    this.imageFilters = res?.imageFilters ?? [];
    // Empty by default — the user picks the size (and every other option) themselves.
    this.printSize = res?.printSize || '';
    this.syncCustomSize();

    // Papers (not needed for frame_only)
    if (this.productTypeSlug === 'frame_only') {
      this.papers = [];
      this.printSurface = null;
    } else {
      this.papers = res?.papers?.data || [];
      this.printSurface = res?.paper || null;
    }

    // Frames
    this.frameStyles = res?.frameStyles?.data || [];
    this.frameMaterials = res?.frameMaterials?.data || [];
    this.frameColors = res?.frameColors || [];
    this.frameWidths = res?.frameWidths || [];
    this.frames = res?.frames?.data || [];
    this.frame = res?.frame || null;
    this.defaultFrame = res?.defaultFrame || '';

    // Set default allowed filters = show ALL until getDynamicFrames narrows them
    this.allowedMaterialIds = this.frameMaterials.map((m: any) => m.id);
    this.allowedStylesIds = this.frameStyles.map((s: any) => s.id);
    this.allowedColors = [...this.frameColors];

    // Set default frame filters — always default to 1" if available, else frame's width
    if (this.frameWidths.includes('1')) {
      this.selectedFrameWidth = '1';
    } else if (this.frame?.width_in) {
      this.selectedFrameWidth = String(this.frame.width_in);
    } else if (this.frameWidths.length) {
      this.selectedFrameWidth = this.frameWidths[0];
    } else {
      this.selectedFrameWidth = '1'; // Hard fallback
    }
    // Default material from frame (auto-select if only 1)
    if (this.frame?.frame_material_id) {
      this.selectedFrameMaterial = this.frame.frame_material_id;
    } else if (this.frameMaterials.length === 1) {
      this.selectedFrameMaterial = this.frameMaterials[0].id;
    }
    // Style + Color = "All" (0 / '') by default
    this.selectedFrameStyle = 0;
    this.selectedFrameColor = '';

    // Glass
    this.glasses = res?.glasses?.data || [];
    this.glass = res?.glass || null;

    // Mounts
    this.matColors = res?.matColors || [];
    this.matWidths = res?.matWidths ?? [];
    this.mounts = res?.mounts?.data || [];
    this.mount1 = res?.mount1 || null;
    this.mount2 = res?.mount2 || null;
    this.mount3 = res?.mount3 || null;
    this.isShowMount = !!(this.mount1);

    // Canvas
    this.canvasWraps = res?.canvasWraps?.data || [];
    this.canvasWrap = res?.canvasWrap || null;

    this.qualities = res?.qualities || [];

    // Set default image based on product type
    this.setDefaultImage();

    this.filterTabs();
    this.updateFrameBorderImage();
    this.onPrintSize();
    this.getDynamicFrames(); // Get allowed materials/styles/colors for current frame width
    this.fetchFilteredFrames(); // Fetch frames filtered by default selected width
    this.calculatePrice();
    this.isLoading = false;
  }

  private fallbackInit() {
    this.pfService.getProductTypes().subscribe({
      next: (res: any) => {
        this.productTypes = res?.data || [];
        this.productType = this.productTypes.find((pt: any) => pt.slug === this.productTypeSlug) || this.productTypes[0];
        // No hardcoded sizes even in the fallback path — sizes are DB-driven only.
        this.printSizes = [];
        this.printSize = '';
        this.syncCustomSize();
        this.setDefaultImage();
        this.filterTabs();
        this.loadIndividualData();
        this.isLoading = false;
      },
      error: () => { this.isLoading = false; }
    });
  }

  private loadIndividualData() {
    if (this.isTabVisible('print')) {
      this.pfService.getPapers().subscribe({ next: (res: any) => { this.papers = res?.data || []; } });
    }
    if (this.isTabVisible('frames')) {
      this.pfService.getFrames([['status', '=', 1]]).subscribe({ next: (res: any) => { this.frames = res?.data || []; } });
      this.pfService.getFrameColors().subscribe({ next: (res: any) => { this.frameColors = (res?.data || []).map((c: any) => c.name); } });
      this.pfService.getFrameWidths().subscribe({ next: (res: any) => { this.frameWidths = (res?.data || []).map((w: any) => w.name); } });
    }
    if (this.isTabVisible('mount')) {
      this.pfService.getMounts().subscribe({ next: (res: any) => { this.mounts = res?.data || []; } });
      this.pfService.getGlasses().subscribe({ next: (res: any) => { this.glasses = res?.data || []; } });
    }
    if (this.isTabVisible('canvas')) {
      this.pfService.getCanvasWraps().subscribe({ next: (res: any) => { this.canvasWraps = res?.data || []; } });
    }
  }

  private setDefaultImage() {
    // Don't overwrite if stock image will be loaded by image_id
    if (this.stockImageId) return;

    const slug = this.productType?.slug || this.productTypeSlug;
    if (slug === 'frame_only') {
      // frame_only uses different placeholder — no image upload needed
      this.imagePath = '';
    } else {
      this.imagePath = '';
    }
  }

  private autoOpenUploadDialog() {
    // On first load the render isn't settled yet, so defer one tick; the shared
    // helper applies the same guards (stock image / already-uploaded / Frame Only)
    // used when the user switches to the Upload tab.
    setTimeout(() => this.openUploadIfNeeded(), 400);
  }

  // ════════════════════════════════════════════════════════
  //  STOCK IMAGE (wall-art search → PDP)
  // ════════════════════════════════════════════════════════

  private loadStockImage(imageId: string) {
    const userId = this.auth.currentUser?.id || 0;
    this.baseApiService.getWallartByImageId(imageId, userId).subscribe({
      next: (res: any) => {
        this.stockImageData = res?.data || res;
        const filename = this.stockImageData?.image_filename;
        if (filename) {
          const imageUrl = 'https://storage.googleapis.com/static-mahattaart-com/400x400/media/' + filename;
          this.imagePath = imageUrl;
          this.myPhotos = [imageUrl];
          this.activePhotoIndex = 0;
          this.updatePreview();
          this.updateCanvasWrapSideBackground();
        }
        // Set quality grade from stock image for price calculation
        if (this.stockImageData?.image_quality_grade) {
          this.qualityGrade = this.stockImageData.image_quality_grade;
          this.calculatePrice();
        }
      },
      error: (err) => console.error('Failed to load stock image:', err)
    });
  }

  // ════════════════════════════════════════════════════════
  //  TABS
  // ════════════════════════════════════════════════════════

  private filterTabs() {
    const slug = this.productType?.slug || this.productTypeSlug;
    this.visibleTabs = this.allTabs
      .filter(t => t.product_type.indexOf(slug) > -1)
      .map(t => {
        // Rename "Upload Image" to "Print Size" for wall_art products
        if (t.slug === 'photo' && this.productSlug === 'wall_art') {
          return { ...t, name: 'Print Size' };
        }
        return t;
      });
    if (!this.visibleTabs.find(t => t.slug === this.activeTab)) {
      this.activeTab = this.visibleTabs[0]?.slug || 'product';
    }
  }

  isTabVisible(slug: string): boolean {
    return this.visibleTabs.some(t => t.slug === slug);
  }

  setTab(slug: string) {
    // Tabs open freely — the user may configure the surface/frame before uploading.
    // The "upload first" rule lives on the Next button (nextTab), and Add to Cart /
    // Buy Now still enforce the image. Blocking direct tab clicks made the page feel
    // dead, so it is deliberately NOT done here.
    this.activeTab = slug;
    // Opening the Upload Image tab pops the uploader straight away.
    if (slug === 'photo') this.openUploadIfNeeded();
  }

  /** Pop the upload dialog when the Upload tab is active and nothing is uploaded yet. */
  private openUploadIfNeeded() {
    if (this.stockImageId) return;
    const currentSlug = this.productType?.slug || this.productTypeSlug;
    if (this.activeTab === 'photo' && this.myPhotos.length === 0 && currentSlug !== 'frame_only') {
      this.uploadDialog.open(this.myPhotos.length, 'Upload Photo');
    }
  }

  nextTab() {
    const currentSlug = this.productType?.slug || this.productTypeSlug;
    // Only the forward step OFF the Upload Image tab needs an image first.
    if (this.activeTab === 'photo' && currentSlug !== 'frame_only' && !this.imagePath) {
      this.showError('Please upload an image before moving to the next step.');
      this.openUploadIfNeeded();
      return;
    }
    const idx = this.visibleTabs.findIndex((t: any) => t.slug === this.activeTab);
    if (idx < this.visibleTabs.length - 1) this.setTab(this.visibleTabs[idx + 1].slug);
  }
  prevTab() {
    const idx = this.visibleTabs.findIndex((t: any) => t.slug === this.activeTab);
    if (idx > 0) this.setTab(this.visibleTabs[idx - 1].slug);
  }
  // Helper for template — expose String() to Angular template
  String = String;

  get isFirstTab(): boolean { return this.visibleTabs[0]?.slug === this.activeTab; }
  get isLastTab(): boolean { return this.visibleTabs[this.visibleTabs.length - 1]?.slug === this.activeTab; }

  // ════════════════════════════════════════════════════════
  //  2. PRODUCT TYPE CHANGE
  // ════════════════════════════════════════════════════════

  onProductType(pt: any) {
    if (this.stockImageId) {
      // For wall_art: re-fetch init data for new product type without full page reload
      this.productTypeSlug = pt.slug;
      this.isLoading = true;
      this.pfService.initProductCustomization(this.productSlug, pt.slug, 0, this.stockImageId).subscribe({
        next: (res: any) => {
          this.handleInitResponse(res);
          // Restore stock image (handleInitResponse may reset it)
          if (this.stockImageData?.image_filename) {
            const imageUrl = 'https://storage.googleapis.com/static-mahattaart-com/400x400/media/' + this.stockImageData.image_filename;
            this.imagePath = imageUrl;
            this.myPhotos = [imageUrl];
            this.activePhotoIndex = 0;
          }
          this.activeTab = 'product';
        },
        error: () => { this.isLoading = false; }
      });
      // Update URL silently
      const qp = new URLSearchParams();
      qp.set('product', this.productSlug);
      qp.set('product_type', pt.slug);
      qp.set('image_id', this.stockImageId);
      this.location.replaceState('/product-customization?' + qp.toString());
    } else {
      this.router.navigate(['/product-customization'], {
        queryParams: { product: this.productSlug, product_type: pt.slug }
      });
    }
  }

  // ════════════════════════════════════════════════════════
  //  3. PRINT SIZE — with validations
  // ════════════════════════════════════════════════════════

  onPrintSizeChange(size: string) {
    this.printSize = size;
    this.syncCustomSize();
    this.onPrintSize();
    this.validatePrintSurface();
    this.frameMarkDisable();
    this.updateUrl();
  }

  private syncCustomSize() {
    if (!this.printSize) return;
    const parts = this.printSize.replace(/\s/g, '').split('X');
    this.printSizeCustomWidth = parts[0] || '';
    this.printSizeCustomHeight = parts[1] || '';
  }

  // 3A. Custom Size Validations
  onCustomWidthChange() {
    let w = parseInt(this.printSizeCustomWidth) || 0;
    if (w < 4 && w > 0) {
      w = 4;
      this.printSizeCustomWidth = '4';
      this.showError('Photo Frame is not available below 4x4');
    }
    if (w > this.maxCustomizeWidth) {
      w = this.maxCustomizeWidth;
      this.printSizeCustomWidth = String(this.maxCustomizeWidth);
    }
    this.applyCustomSize();
  }

  onCustomHeightChange() {
    let h = parseInt(this.printSizeCustomHeight) || 0;
    if (h < 4 && h > 0) {
      h = 4;
      this.printSizeCustomHeight = '4';
      this.showError('Photo Frame is not available below 4x4');
    }
    if (h > this.maxCustomizeHeight) {
      h = this.maxCustomizeHeight;
      this.printSizeCustomHeight = String(this.maxCustomizeHeight);
    }
    this.applyCustomSize();
  }

  private applyCustomSize() {
    if (!this.printSizeCustomWidth || !this.printSizeCustomHeight) return;
    // 3B. Canvas minimum size
    if (!this.checkCanvasSize()) return;
    this.printSize = this.printSizeCustomWidth + ' X ' + this.printSizeCustomHeight;
    this.onPrintSize();
    this.validatePrintSurface();
    this.frameMarkDisable();
    this.updateUrl();
  }

  // ════════════════════════════════════════════════════════
  //  VALIDATIONS
  // ════════════════════════════════════════════════════════

  // 3B. Canvas Minimum Size
  private checkCanvasSize(): boolean {
    const slug = this.productType?.slug || this.productTypeSlug;
    const w = parseInt(this.printSizeCustomWidth) || 0;
    const h = parseInt(this.printSizeCustomHeight) || 0;
    if (slug === 'canvas' && (w < 8 || h < 8)) {
      this.showError('Canvas is not available below size 8"x8", We are moving you to Photo Frames.');
      // Auto-switch to framing
      const framing = this.productTypes.find((pt: any) => pt.slug === 'framing');
      if (framing) this.onProductType(framing);
      return false;
    }
    return true;
  }

  // 3C. Print Surface Auto-Switch (Glossy > 8 inch)
  private validatePrintSurface() {
    if (!this.printSurface) return;
    const w = parseInt(this.printSizeCustomWidth) || 0;
    const h = parseInt(this.printSizeCustomHeight) || 0;
    if (this.printSurface.display_name === 'Photographic Glossy Paper' && (w >= 8 || h >= 8)) {
      const matt = this.papers.find((p: any) => p.display_name === 'Matt 189 GSM');
      if (matt) {
        const oldName = this.printSurface.display_name;
        this.printSurface = matt;
        this.showError(`Print Surface changed from ${oldName} to Matt 189 GSM. ${oldName} doesn't support size more than 8 inch.`);
      }
    }
  }

  // 3D. Transparent Mount Restrictions
  private validateTransparentMount(mount: any): boolean {
    if (mount?.name !== 'Transparent Mount') return true;

    // Frame too small
    if (this.frame?.width_in == 0.5) {
      this.showError('Transparent mount is not available for frames under 0.5 inches');
      return false;
    }

    // Total size too large
    if (this.checkGlassMountApplicable()) {
      this.showError('Transparent mount is not available for total sizes above 12x15 inches');
      return false;
    }
    return true;
  }

  // Check if glass/mount is applicable (final size > 12x15)
  private checkGlassMountApplicable(): boolean {
    const w = parseInt(this.printSizeCustomWidth) || 0;
    const h = parseInt(this.printSizeCustomHeight) || 0;
    const mountAdd = this.mount1 ? (this.mountSize1 || 0) * 2 : 0;
    const frameAdd = (this.frame?.width_in || this.frame?.width || 0) * 2;
    const fw = w + mountAdd + frameAdd;
    const fh = h + mountAdd + frameAdd;
    return (fw > 12 && fh > 15) || (fw > 15 && fh > 12);
  }

  // 3E. Table Frame Restriction
  onTableFrameChange(checked: boolean) {
    if (checked) {
      const w = parseInt(this.printSizeCustomWidth) || 0;
      const h = parseInt(this.printSizeCustomHeight) || 0;
      if (w <= 15 && h <= 15) {
        this.isTableFrame = 1;
        this.calculatePrice();
      } else {
        this.isTableFrame = 0;
        this.showError('Table frame not available above 15X15');
      }
    } else {
      this.isTableFrame = 0;
      this.calculatePrice();
    }
  }

  // 3G. Frame Disable Logic
  private frameMarkDisable() {
    if (!this.frames.length) return;
    const w = parseInt(this.printSizeCustomWidth) || 0;
    const h = parseInt(this.printSizeCustomHeight) || 0;
    // No print size chosen yet → keep every frame selectable. The size restriction
    // only makes sense once there is a real size to check against; without it
    // frameW/H = (frame width × 2) alone, which sits below every min_restriction
    // and greyed out ALL frames, so nothing was clickable. Restrictions re-apply
    // as soon as a size is picked (this runs again on size/mount change).
    if (w <= 0 || h <= 0) {
      this.frames.forEach((f: any) => f.disabled = false);
      return;
    }
    const mountAdd = this.mount1 ? (this.mountSize1 || 0) * 2 : 0;
    const expectedW = w + mountAdd;
    const expectedH = h + mountAdd;

    // Parse a "WxH" restriction robustly — case-insensitive x/×, decimals, stray
    // \r/spaces (parseFloat tolerates trailing junk). null = unparseable → the
    // restriction is ignored rather than disabling the frame. Mirrors the sales
    // panel's parseWH.
    const parseWH = (s: any): [number, number] | null => {
      if (!s) return null;
      const p = String(s).split(/[x×]/i).map((v) => parseFloat(v));
      return p.length >= 2 && isFinite(p[0]) && isFinite(p[1]) ? [p[0], p[1]] : null;
    };

    this.frames.forEach((f: any) => {
      const ow = expectedW + (f.width_in || f.width || 0) * 2;
      const oh = expectedH + (f.width_in || f.width || 0) * 2;
      const sec = parseWH(f.secondary_restriction);
      const min = parseWH(f.min_restriction);
      // Orientation-agnostic: a portrait print must still fit a landscape max and
      // vice-versa. The old check only compared one orientation, so valid frames
      // greyed out. Mirrors the React panel exactly.
      const tooBig = !!sec && !((sec[0] >= ow && sec[1] >= oh) || (sec[0] >= oh && sec[1] >= ow));
      const tooSmall = !!min && !((ow >= min[0] && oh >= min[1]) || (oh >= min[0] && ow >= min[1]));
      f.disabled = tooBig || tooSmall;
    });
  }

  // ════════════════════════════════════════════════════════
  //  PRINT SURFACE
  // ════════════════════════════════════════════════════════

  onPrintSurface(paper: any) {
    this.printSurface = paper;
    this.validatePrintSurface();
    this.updateUrl();
  }

  get glossyPapers(): any[] { return this.dedupeByName(this.papers.filter((p: any) => p.category_id == 2)); }
  get mattePapers(): any[] { return this.dedupeByName(this.papers.filter((p: any) => p.category_id == 1)); }

  /**
   * One surface per display_name — mirrors the sales panel's grouping so the same
   * design isn't listed multiple times (a surface has several roll-width rows in
   * `paper`, and the site returns them all). Normalised (lowercase, spaces removed)
   * so "Glossy 235GSM" and "Glossy 235 GSM" collapse to one. First row wins; its
   * display_name drives the roll lookup at price time, so which row is kept doesn't
   * change the price.
   */
  private dedupeByName(list: any[]): any[] {
    const seen = new Set<string>();
    return list.filter((p: any) => {
      const key = String(p.display_name ?? '').toLowerCase().replace(/\s+/g, '');
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }

  // ════════════════════════════════════════════════════════
  //  FRAME
  // ════════════════════════════════════════════════════════

  onFrame(f: any) {
    if (f.disabled) return;
    this.frame = f;
    this.updateFrameBorderImage();
    this.onPrintSize();
    this.updateUrl();
  }

  // ── Frame Size click (resets ALL other filters) ──
  onFrameWidthChange(w: string) {
    this.selectedFrameWidth = w; // Always set, never toggle off
    this.selectedFrameMaterial = 0;  // RESET
    this.selectedFrameStyle = 0;     // RESET
    this.selectedFrameColor = '';    // RESET
    this.getFrames();
  }

  // ── Frame Material click (keeps width, style, color) ──
  onFrameMaterialChange(id: number) {
    this.selectedFrameMaterial = id;
    this.getFrames();
  }

  // ── Frame Style click (keeps width, material, color) ──
  onFrameStyleChange(id: number) {
    this.selectedFrameStyle = id;
    this.getFrames();
  }

  // ── Frame Color click (keeps width, material, style) ──
  onFrameColorChange(c: string) {
    this.selectedFrameColor = c;
    this.getFrames();
  }

  // ── getFrames: calls both API-B (dynamic options) + API-C (filtered list) ──
  private getFrames() {
    this.getDynamicFrames();
    this.fetchFilteredFrames();
  }

  // API-B: Get which materials/styles/colors are allowed
  // Params: width + material + style (NOT color)
  private getDynamicFrames() {
    const width = this.selectedFrameWidth || '1';
    this.pfService.getCustomizedFrames(width, this.selectedFrameMaterial, this.selectedFrameStyle).subscribe({
      next: (res: any) => {
        // Update allowed lists
        if (res?.materialIds) this.allowedMaterialIds = res.materialIds;
        if (res?.stylesId) this.allowedStylesIds = res.stylesId;
        if (res?.colors) this.allowedColors = res.colors;

        // Auto-select if only 1 option available
        if (this.allowedMaterialIds.length === 1) {
          this.selectedFrameMaterial = this.allowedMaterialIds[0];
        }
        if (this.allowedStylesIds.length === 1) {
          this.selectedFrameStyle = this.allowedStylesIds[0];
        }
        if (this.allowedColors.length === 1) {
          this.selectedFrameColor = this.allowedColors[0];
        }
      },
      error: () => {
        // On error, show all
        this.allowedMaterialIds = this.frameMaterials.map((m: any) => m.id);
        this.allowedStylesIds = this.frameStyles.map((s: any) => s.id);
        this.allowedColors = [...this.frameColors];
      }
    });
  }

  // API-C: Fetch filtered frame list
  private fetchFilteredFrames() {
    const filter: any[] = [['status', '=', 1]];
    if (this.selectedFrameWidth) {
      filter.push(['width_in', '=', parseFloat(this.selectedFrameWidth)]);
    }
    if (this.selectedFrameMaterial > 0) {
      filter.push(['frame_material_id', '=', this.selectedFrameMaterial]);
    }
    if (this.selectedFrameStyle > 0) {
      filter.push(['frame_style_id', '=', this.selectedFrameStyle]);
    }
    if (this.selectedFrameColor) {
      filter.push(['color', '=', this.selectedFrameColor]);
    }
    if (this.isTableFrame === 1) {
      filter.push(['is_table_frame', '=', 1]);
    }

    this.pfService.getFrames(filter).subscribe({
      next: (res: any) => {
        this.frames = res?.data || [];
        this.frameMarkDisable();

        // Empty by default — NEVER auto-pick a frame. Only drop a prior selection
        // the new filters have excluded, so Product Overview shows "—" until the
        // user chooses a frame themselves.
        if (this.frame) {
          const stillInList = this.frames.find((f: any) => f.id === this.frame.id);
          if (!stillInList) {
            this.frame = null;
            this.updateFrameBorderImage();
          }
        }
      }
    });
  }

  private updateFrameBorderImage() {
    if (!this.frame) {
      this.frameBorderImage = '';
      this.frameBorderWidth = 0;
      return;
    }
    const widthIn = this.frame.width_in || this.frame.width || 1;
    this.frameBorderWidth = widthIn * this.FRAME_BORDER_CALC;

    if (this.frame.whole_frame_image) {
      const slice = this.frame.image_slice || 30;
      this.frameBorderImage = `url("${this.frame.whole_frame_image}") ${slice} stretch stretch`;
    } else if (this.frame.L_frame_image || this.frame.image) {
      const img = this.frame.L_frame_image || this.frame.image;
      this.frameBorderImage = `url(${img}) 30 round`;
    } else {
      this.frameBorderImage = '';
    }

    // Auto-remove glass mount if frame width is 0.5" or total size > 12x15
    if (widthIn == 0.5 || this.checkGlassMountApplicable()) {
      this.removeGlassMount();
    }
  }

  private removeGlassMount() {
    // Only clear transparent mounts, keep glass
    if (this.mount1?.name === 'Transparent Mount') this.mount1 = null;
    if (this.mount2?.name === 'Transparent Mount') this.mount2 = null;
    if (this.mount3?.name === 'Transparent Mount') this.mount3 = null;
  }

  // ════════════════════════════════════════════════════════
  //  GLASS
  // ════════════════════════════════════════════════════════

  onGlass(g: any) {
    this.glass = g;
    this.updateUrl();
  }

  // ════════════════════════════════════════════════════════
  //  MOUNT
  // ════════════════════════════════════════════════════════

  showMount(show: boolean) {
    this.isShowMount = show;
    if (show) {
      // Default: width 0.5", color "Ice White"
      this.mountSize1 = 0.5;
      const iceWhite = this.mounts.find((m: any) => m.name === 'Ice White');
      if (iceWhite) {
        this.onMount(iceWhite, 1);
        return; // onMount already calls onPrintSize + updateUrl
      } else if (this.mounts.length > 0) {
        this.onMount(this.mounts[0], 1);
        return;
      }
    } else {
      this.mount1 = null; this.mount2 = null; this.mount3 = null;
      this.mat1Padding = 0; this.mat2Padding = 0; this.mat3Padding = 0;
      this.mount1Background = '#fff'; this.mount2Background = '#fff'; this.mount3Background = '#fff';
    }
    this.onPrintSize();
    this.updateUrl();
  }

  onMount(mount: any, layer: number) {
    // 3D. Validate transparent mount
    if (!this.validateTransparentMount(mount)) return;

    // Set background from mount texture
    const bg = mount.image_1
      ? `rgba(0,0,0,0) url(${mount.image_1}) repeat scroll 0% 0%`
      : (mount.hex || mount.color || '#fff');

    // Transparent Mount → auto-select "No Glass"
    if (mount.name === 'Transparent Mount') {
      const noGlass = this.glasses.find((g: any) => g.name === 'No Glass');
      if (noGlass) this.glass = noGlass;
    }

    if (layer === 1) { this.mount1 = mount; this.mount1Background = bg; }
    else if (layer === 2) { this.mount2 = mount; this.mount2Background = bg; }
    else if (layer === 3) { this.mount3 = mount; this.mount3Background = bg; }

    this.onPrintSize();
    this.updateUrl();
  }

  onMountSizeChange(layer: number, value: number) {
    if (layer === 1) this.mountSize1 = value;
    else if (layer === 2) this.mountSize2 = value;
    else if (layer === 3) this.mountSize3 = value;
    this.onPrintSize();
    this.updateUrl();
  }

  // ════════════════════════════════════════════════════════
  //  CANVAS
  // ════════════════════════════════════════════════════════

  onCanvasWrap(wrap: any) {
    this.canvasWrap = wrap;
    if (wrap.id === 2 || wrap.slug === 'gallery') {
      this.canvasWrapItem = 'gallery-mirror';
    } else {
      this.canvasWrapItem = '';
    }
    this.updateCanvasWrapSideBackground();
    this.updateUrl();
  }

  private updateCanvasWrapSideBackground() {
    if (!this.canvasWrap) return;
    if (this.canvasWrap.slug === 'museum') {
      this.canvasWrapSideBackground = '#000';
    } else {
      // Gallery wrap: mirror/blur of image
      if (this.imagePath) {
        this.canvasWrapSideBackground = `url(${this.imagePath})`;
      } else {
        this.canvasWrapSideBackground = '#333';
      }
    }
  }

  // ════════════════════════════════════════════════════════
  //  UPLOAD PHOTOS
  // ════════════════════════════════════════════════════════

  onAddPhoto(event: Event) {
    this.uploadDialog.open(this.myPhotos.length, 'Upload Photo');
  }

  onPhoto(index: number) {
    this.activePhotoIndex = index;
    this.imagePath = this.myPhotos[index];
    this.updatePreview();
    this.updateCanvasWrapSideBackground();
  }

  onRemovePhoto(event: Event, index: number) {
    event.stopPropagation();
    if (index === this.activePhotoIndex && this.myPhotos.length > 1) {
      // Can't delete active image without switching
      this.showError("You can't delete current active image.");
      return;
    }
    this.myPhotos.splice(index, 1);
    if (this.activePhotoIndex >= this.myPhotos.length) {
      this.activePhotoIndex = Math.max(0, this.myPhotos.length - 1);
    }
    this.imagePath = this.myPhotos[this.activePhotoIndex] || '';
    this.updatePreview();
  }

  // ════════════════════════════════════════════════════════
  //  2. LIVE PREVIEW CALCULATIONS
  // ════════════════════════════════════════════════════════

  private onPrintSize() {
    const w = parseInt(this.printSizeCustomWidth) || 12;
    const h = parseInt(this.printSizeCustomHeight) || 18;
    const slug = this.productType?.slug || this.productTypeSlug;
    const frameWidthIn = (slug === 'framing' || slug === 'frame_only') ? (this.frame?.width_in || this.frame?.width || 0) : 0;
    const mount1In = this.mount1 ? this.mountSize1 : 0;
    const mount2In = this.mount2 ? this.mountSize2 : 0;
    const mount3In = this.mount3 ? (this.mountSize3 || 0) : 0;
    const matFrameInch = (mount1In + mount2In + mount3In + frameWidthIn) * 2;

    const isMobile = window.innerWidth < 768;
    const container = isMobile ? this.PREVIEW_CONTAINER_MOBILE : this.PREVIEW_CONTAINER;
    const matCalc = isMobile ? 5 : 6;
    let perInchPx: number;

    if (w >= h) {
      perInchPx = container / (w + matFrameInch);
    } else {
      perInchPx = container / (h + matFrameInch);
    }

    this.previewW = Math.round(w * perInchPx) + 'px';
    this.previewH = Math.round(h * perInchPx) + 'px';
    this.frameBorderWidth = Math.round(frameWidthIn * perInchPx) || 0;
    this.mat1Padding = this.mount1 ? Math.round(mount1In * perInchPx) : 0;
    this.mat2Padding = this.mount2 ? Math.round(mount2In * perInchPx) : 0;
    this.mat3Padding = this.mount3 ? Math.round(mount3In * perInchPx) : 0;

    // Room preview sizes
    this.previewRoomW = Math.round(w * this.ROOM_INCH) + 'px';
    this.previewRoomH = Math.round(h * this.ROOM_INCH) + 'px';

    this.calculatePrice();
  }

  updatePreview() {
    this.updateFrameBorderImage();
    // Mount backgrounds
    if (this.mount1 && !this.mount1Background.startsWith('url') && !this.mount1Background.startsWith('rgba')) {
      this.mount1Background = this.mount1.hex || this.mount1.color || '#fff';
    }
    if (this.mount2 && !this.mount2Background.startsWith('url') && !this.mount2Background.startsWith('rgba')) {
      this.mount2Background = this.mount2.hex || this.mount2.color || '#fff';
    }
    if (this.mount3 && !this.mount3Background.startsWith('url') && !this.mount3Background.startsWith('rgba')) {
      this.mount3Background = this.mount3.hex || this.mount3.color || '#fff';
    }
  }

  get previewLayout(): string {
    const slug = this.productType?.slug || this.productTypeSlug;
    if (slug === 'canvas') return 'canvas';
    if (slug === 'print_only') return 'print';
    return 'frame';
  }

  get defaultPlaceholder(): string {
    const slug = this.productType?.slug || this.productTypeSlug;
    if (slug === 'frame_only') return 'assets/images/mahattaart/product_icons/upload_photos_frame.jpg';
    return 'assets/images/mahattaart/product_icons/upload_photos.jpg';
  }

  // Backward compat getters
  get printSizeW(): string { return this.previewW; }
  get printSizeH(): string { return this.previewH; }

  // Room
  toggleRoomPreview() { this.showRoomPreview = !this.showRoomPreview; }
  selectRoom(slug: string) { this.roomType = slug; }
  get activeRoom(): any { return this.rooms.find(r => r.slug === this.roomType) || this.rooms[0]; }

  // Frame list (already filtered from API, just return frames)
  get filteredFrames(): any[] {
    return this.frames;
  }

  // ── Image filter (display effect) + rounded shape ──
  /** The CSS `filter` for the currently selected image filter ('' = none). */
  get imageFilterCss(): string {
    return this.imageFilters.find(f => f.name === this.selectedImageFilter)?.css || '';
  }
  selectImageFilter(name: string) { this.selectedImageFilter = name || ''; }
  /** Rounded shape only applies where there is a frame (framing / frame_only). */
  get canRoundShape(): boolean {
    const slug = this.productType?.slug || this.productTypeSlug;
    return slug === 'framing' || slug === 'frame_only';
  }
  setRoundedShape(v: boolean) { this.roundedShape = v; }

  // ════════════════════════════════════════════════════════
  //  4. PRICE CALCULATION
  // ════════════════════════════════════════════════════════

  private calculatePrice() {
    if (!this.productType || !this.printSize) {
      console.log('🔥 PRICE SKIP: productType=', this.productType, 'printSize=', this.printSize);
      return;
    }
    const slug = this.productType.slug || this.productTypeSlug;

    // Early return if no paper and not frame_only
    if (!this.printSurface && slug !== 'frame_only') {
      console.log('🔥 PRICE SKIP: no printSurface, slug=', slug);
      return;
    }

    this.priceLoading = true;
    const imageSize = this.printSize.replace(/\s/g, '');
    const params: any = {
      product_id: this.product?.id || 0,
      product_type: this.productType?.id || 0,
      paper_id: this.printSurface?.id || 0,
      frame_id: (slug === 'framing' || slug === 'frame_only') ? (this.frame?.id || 0) : 0,
      mount_id_1: this.mount1?.id || 0,
      mount_id_2: this.mount2?.id || 0,
      mount_id_3: this.mount3?.id || 0,
      mount_size_1: this.mount1 ? this.mountSize1 : 0,
      mount_size_2: this.mount2 ? this.mountSize2 : 0,
      mount_size_3: this.mount3 ? this.mountSize3 : 0,
      glass_id: this.glass?.id || 0,
      image_size: imageSize,
      quality_grade: this.qualityGrade,
      is_table_frame: this.isTableFrame
    };

    console.log('🔥 PRICE API params:', params);
    this.pfService.getProductPrice(params).subscribe({
      next: (res: any) => {
        console.log('🔥 PRICE API response:', res);
        const d = res?.data || {};
        if (d.total || d.amount_payable) {
          this.pricingDetails = d;
          this.payable = d.amount_payable || d.total || 0;
          this.discount = d.discount_with_tax || d.discount || 0;
        } else {
          this.payable = 0;
          this.discount = 0;
        }

        // 3F. Handle frame restriction from API
        if (res?.restriction?.frame) {
          this.showError(`Sorry! ${this.frame?.frame_name || this.frame?.name} frame cannot be selected... moving you to ${res.restriction.frame.frame_name || res.restriction.frame.name}`);
          this.frame = res.restriction.frame;
          this.updateFrameBorderImage();
        }
        if (res?.error) {
          this.showError(res.error);
        }

        this.priceLoading = false;
      },
      error: () => { this.priceLoading = false; }
    });
  }

  // Backward compat getters for template
  get mrp(): number | null { return this.discount > 0 ? this.payable + this.discount : null; }
  get effectivePrice(): number | null { return this.payable || null; }
  get offerPercent(): number | null { return this.pricingDetails?.discount_rate > 1 ? this.pricingDetails.discount_rate : null; }
  get price(): number | null { return this.payable || null; }

  // ════════════════════════════════════════════════════════
  //  5. ADD TO CART / BUY NOW
  // ════════════════════════════════════════════════════════

  // VALIDATION LAYER 1: Button enabled check
  get canAddToCart(): boolean {
    return !!this.payable && this.payable > 0 && this.printSizes.length > 0;
  }

  // Check if user has uploaded an image
  private isImageUploaded(): boolean {
    return !!this.imagePath && this.imagePath !== this.defaultPlaceholder;
  }

  // Show validation dialog via global service (rendered at app root level)
  private showValidationDialog(title: string, message: string, icon: 'error' | 'warning' | 'success' = 'error') {
    this.validationDialog.show(title, message, icon);
  }

  // ── Full validation before add to cart ──
  private validateBeforeCart(): boolean {
    const slug = this.productType?.slug || this.productTypeSlug;

    // Check 1: Price must be calculated
    if (!this.payable || this.payable <= 0) {
      this.showValidationDialog('Price not available', 'Please select all required options to calculate the price.', 'warning');
      return false;
    }

    // Check 2: Print size must be selected
    if (!this.printSize || this.printSizes.length === 0) {
      this.showValidationDialog('No size selected', 'Please select an image size before proceeding.', 'warning');
      return false;
    }

    // Check 3: Image must be uploaded (except frame_only)
    if (!this.isImageUploaded() && slug !== 'frame_only') {
      this.showValidationDialog('No image chosen, yet.', 'Please upload or choose one.', 'error');
      return false;
    }

    // Check 4: Print surface required (except frame_only)
    if (!this.printSurface && slug !== 'frame_only') {
      this.showValidationDialog('No print surface selected', 'Please select a print surface.', 'warning');
      return false;
    }

    // Check 5: Frame required for framing/frame_only
    if ((slug === 'framing' || slug === 'frame_only') && !this.frame) {
      this.showValidationDialog('No frame selected', 'Please select a frame from the Frames tab.', 'warning');
      return false;
    }

    // Check 6: Canvas wrap required for canvas
    if (slug === 'canvas' && !this.canvasWrap) {
      this.showValidationDialog('No canvas wrap selected', 'Please select a canvas wrap option.', 'warning');
      return false;
    }

    return true;
  }

  // ── onAddToCart — Main entry point ──
  onAddToCart(isUpdate: number, isRedirect: number) {
    // Guard against a double trigger — a rapid double-tap, or Buy Now and Add To
    // Cart both firing from one press. The first call owns the request; any
    // re-entry while it is still in flight is ignored, so one click = one cart row.
    if (this.addingToCart) return;

    // Run all validations
    if (!this.validateBeforeCart()) return;

    this.addingToCart = true;
    const cart = this.buildCartObject();

    this.state.apiAddToCart(cart).subscribe({
      next: (res: any) => {
        this.addingToCart = false;
        this.state.loadCartFromApi(true);

        if (isRedirect === 1) {
          // Buy Now goes straight to checkout — no "added to cart" toast, so it
          // never looks like Add To Cart also ran.
          this.router.navigate(['/checkout']);
        } else {
          this.cartSuccess = true;
          setTimeout(() => this.cartSuccess = false, 4000);
        }
      },
      error: (err: any) => {
        this.addingToCart = false;
        console.error('🛒 Add to cart error:', err);
        const msg = err?.error?.message || err?.error?.error || err?.message || 'Failed to add to cart. Please try again.';
        this.showValidationDialog('Something went wrong', msg, 'error');
      }
    });
  }

  // Convenience methods for template
  addToCart() { this.onAddToCart(0, 0); }
  buyNow() { this.onAddToCart(0, 1); }

  // Build the full cart payload matching the API spec
  private buildCartObject(): any {
    const imageSize = this.printSize.replace(/\s/g, '');
    const slug = this.productType?.slug || this.productTypeSlug;

    return {
      customer_id: this.auth.currentUser?.id || 0,
      product_id: this.product?.id || 0,
      product_type_id: this.productType?.id || 0,
      no_of_product_types: 1,
      quantity: 1,
      product_category_id: this.product?.product_category_id || 0,
      layout_id: 0,
      filter: slug,
      url: this.buildUrl(),
      customizedProductItems: [{
        product_category_id: this.product?.product_category_id || 0,
        frame_id: (slug === 'framing' || slug === 'frame_only') ? (this.frame?.id || 0) : 0,
        frame_rate: this.frame?.length_rate_ft || 0,
        mount_id_1: this.mount1?.id || 0,
        mount_rate_1: this.mount1?.mount_rate || 0,
        mount_size_1: this.mount1 ? this.mountSize1 : 0,
        mount_id_2: this.mount2?.id || 0,
        mount_rate_2: this.mount2?.mount_rate || 0,
        mount_size_2: this.mount2 ? this.mountSize2 : 0,
        mount_id_3: this.mount3?.id || 0,
        mount_rate_3: this.mount3?.mount_rate || 0,
        mount_size_3: this.mount3 ? this.mountSize3 : 0,
        paper_id: this.printSurface?.id || 0,
        print_rate: this.printSurface?.rate || this.printSurface?.print_rate || 0,
        image_size: imageSize,
        canvas_wrap_id: this.canvasWrap?.id || 0,
        canvas_rate: this.canvasWrap?.rate || this.canvasWrap?.canvas_rate || 0,
        glass_id: this.glass?.id || 0,
        glass_rate: this.glass?.rate || this.glass?.glass_rate || 0,
        is_table_frame: this.isTableFrame,
        rounded_shape: this.canRoundShape ? this.roundedShape : false,
        final_size: this.finalFrameSize,
        price: this.payable || 0,
        quantity: 1,
        image_detail: {
          image_id: this.stockImageId || null,
          image_path: this.imagePath || '',
          image_caption: this.stockImageData?.image_caption || null,
          image_photographer: this.stockImageData?.image_photographer || null,
          size: imageSize,
          quality_grade: this.qualityGrade,
          filter: this.selectedImageFilter || ''
        }
      }]
    };
  }

  // ════════════════════════════════════════════════════════
  //  8. URL STATE MANAGEMENT
  // ════════════════════════════════════════════════════════

  private updateUrl() {
    this.calculatePrice();
    const url = this.buildUrl();
    this.location.replaceState(url);
  }

  private buildUrl(): string {
    const imageSize = this.printSize.replace(/\s/g, '');
    const params = new URLSearchParams();
    params.set('product', this.productSlug);
    params.set('product_type', this.productType?.slug || this.productTypeSlug);
    if (this.stockImageId) params.set('image_id', this.stockImageId);
    if (this.printSurface) params.set('print_surface', String(this.printSurface.id));
    params.set('image_size', imageSize);
    if (this.frame) params.set('frame_code', this.frame.code || this.frame.id);
    if (this.mount1) { params.set('mount1_code', this.mount1.code || this.mount1.id); params.set('mount1_size', String(this.mountSize1)); }
    if (this.mount2) { params.set('mount2_code', this.mount2.code || this.mount2.id); params.set('mount2_size', String(this.mountSize2)); }
    if (this.glass) params.set('glass_type', this.glass.code || this.glass.id);
    params.set('is_table_frame', String(this.isTableFrame));
    return '/product-customization?' + params.toString();
  }

  // ════════════════════════════════════════════════════════
  //  HELPERS
  // ════════════════════════════════════════════════════════

  get productTypeLabel(): string {
    return this.productType?.name || {
      framing: 'Photo Frame', print_only: 'Only Print', frame_only: 'Frame Only', canvas: 'Canvas'
    }[this.productTypeSlug] || 'Customization';
  }

  get finalFrameSize(): string {
    if (!this.printSize) return '';
    const parts = this.printSize.replace(/\s/g, '').split('X');
    const w = parseInt(parts[0]) || 0;
    const h = parseInt(parts[1]) || 0;
    const mountAdd = this.mount1 ? (this.mountSize1 || 0) * 2 : 0;
    const frameAdd = (this.frame?.width_in || this.frame?.width || 0) * 2;
    const fw = w + mountAdd + frameAdd;
    const fh = h + mountAdd + frameAdd;
    return fw && fh ? fw + '" X ' + fh + '"' : '';
  }

  private showError(msg: string) {
    this.errorMsg = msg;
    setTimeout(() => this.errorMsg = '', 5000);
  }

  scrollReviews(dir: number) {
    this.reviewScrollIndex = Math.max(0, Math.min(this.reviews.length - 1, this.reviewScrollIndex + dir));
  }

  // Shortcut to form controls for template
  get f() { return this.queryForm.controls; }

  submitQuery() {
    // Mark all fields as touched to trigger validation messages
    this.queryForm.markAllAsTouched();
    if (this.queryForm.invalid) return;

    this.formSubmitting = true;
    this.formError = false;

    const payload = {
      ...this.queryForm.value,
      subject: 'Product Customization Query',
      page_url: this.router.url
    };

    this.http.post(`${environment.apiUrl}/api/unauth/contact`, payload).subscribe({
      next: () => {
        this.formSubmitting = false;
        this.formSuccess = true;
        this.queryForm.reset();
        setTimeout(() => this.formSuccess = false, 10000);
      },
      error: () => {
        this.formSubmitting = false;
        this.formError = true;
        setTimeout(() => this.formError = false, 10000);
      }
    });
  }
}
